import InputError from '@/components/input-error';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Spinner } from '@/components/ui/spinner';
import * as profileRoutes from '@/routes/profile';
import * as myProfileRoutes from '@/routes/my-profile';
import { type Gender } from '@/types';
import { router } from '@inertiajs/react';
import axios from 'axios';
import { CheckCircle } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

// ─── Constants ────────────────────────────────────────────────────────────────

const STORAGE_KEY = 'huren_wizard_v1';

export type WizardStep = 'basics' | 'bio' | 'rates' | 'contact' | 'register';

const STEP_IDS: WizardStep[] = [
  'basics',
  'bio',
  'rates',
  'contact',
  'register',
];

interface RateRow {
  duration: string;
  price: string;
}

interface WizardState {
  display_name: string;
  gender: Gender;
  age: string;
  nationality: string;
  city: string;
  country: string;
  languages: string[];
  subtitle_en: string;
  description_en: string;
  rates_in_call: RateRow[];
  rates_out_call: RateRow[];
  contacts_phone: string;
  contacts_telegram: string;
}

const EMPTY: WizardState = {
  display_name: '',
  gender: 'female',
  age: '',
  nationality: '',
  city: '',
  country: '',
  languages: [],
  subtitle_en: '',
  description_en: '',
  rates_in_call: [],
  rates_out_call: [],
  contacts_phone: '',
  contacts_telegram: '',
};

// Language options — codes are ISO 639-1, labels are in the target language itself
// so a Russian speaker sees "Русский" not "Russian".
const LANGUAGE_OPTIONS = [
  { code: 'en', label: 'English' },
  { code: 'de', label: 'Deutsch' },
  { code: 'nl', label: 'Nederlands' },
  { code: 'fr', label: 'Français' },
  { code: 'es', label: 'Español' },
  { code: 'it', label: 'Italiano' },
  { code: 'ru', label: 'Русский' },
  { code: 'uk', label: 'Українська' },
  { code: 'pl', label: 'Polski' },
  { code: 'ro', label: 'Română' },
  { code: 'vi', label: 'Tiếng Việt' },
  { code: 'pt', label: 'Português' },
  { code: 'ar', label: 'العربية' },
];

const DURATION_OPTIONS = [
  { value: '30', label: '30 min' },
  { value: '60', label: '1 h' },
  { value: '90', label: '90 min' },
  { value: '120', label: '2 h' },
  { value: '180', label: '3 h' },
  { value: '240', label: '4 h' },
  { value: '1440', label: '★' }, // "Overnight" — rendered as label in each locale
];

// ─── Storage helpers ──────────────────────────────────────────────────────────

function loadFromStorage(): WizardState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return EMPTY;
    return { ...EMPTY, ...JSON.parse(raw) };
  } catch {
    return EMPTY;
  }
}

function saveToStorage(state: WizardState) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {}
}

export function clearWizardStorage() {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {}
}

function getCsrf(): string {
  return (
    document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')
      ?.content ?? ''
  );
}

// ─── Per-step validation ──────────────────────────────────────────────────────

function validateStep(
  step: WizardStep,
  form: WizardState,
  reg: {
    name: string;
    email: string;
    password: string;
    passwordConfirm: string;
  },
  t: (key: string) => string,
): Record<string, string> {
  const errors: Record<string, string> = {};

  if (step === 'basics') {
    if (!form.display_name.trim()) {
      errors.display_name = t('wizard.err_display_name_required');
    } else if (form.display_name.trim().length < 2) {
      errors.display_name = t('wizard.err_display_name_min');
    }
    if (form.age && (Number(form.age) < 18 || Number(form.age) > 99)) {
      errors.age = t('wizard.err_age_range');
    }
  }

  if (step === 'bio') {
    if (form.subtitle_en.length > 200) {
      errors.subtitle_en = t('wizard.err_tagline_max');
    }
    if (form.description_en.length > 5000) {
      errors.description_en = t('wizard.err_description_max');
    }
  }

  if (step === 'rates') {
    [...form.rates_in_call, ...form.rates_out_call].forEach((row, i) => {
      if (row.price !== '' && Number(row.price) < 0) {
        errors[`rate_${i}`] = t('wizard.err_price_negative');
      }
    });
  }

  if (step === 'contact') {
    if (!form.contacts_phone.trim() && !form.contacts_telegram.trim()) {
      errors.contacts = t('wizard.err_contact_required');
    }
  }

  if (step === 'register') {
    if (!reg.name.trim()) errors.name = t('wizard.err_name_required');
    if (!reg.email.trim()) {
      errors.email = t('wizard.err_email_required');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(reg.email)) {
      errors.email = t('wizard.err_email_invalid');
    }
    if (!reg.password) {
      errors.password = t('wizard.err_password_required');
    } else if (reg.password.length < 8) {
      errors.password = t('wizard.err_password_min');
    }
    if (reg.password !== reg.passwordConfirm) {
      errors.password_confirmation = t('wizard.err_password_mismatch');
    }
  }

  return errors;
}

// ─── Rate table ───────────────────────────────────────────────────────────────

function RateTable({
  label,
  rows,
  errorPrefix,
  stepErrors,
  onChange,
}: {
  label: string;
  rows: RateRow[];
  errorPrefix: string;
  stepErrors: Record<string, string>;
  onChange: (rows: RateRow[]) => void;
}) {
  const { t } = useTranslation();

  const addRow = () => onChange([...rows, { duration: '60', price: '' }]);
  const removeRow = (i: number) => onChange(rows.filter((_, idx) => idx !== i));
  const updateRow = (i: number, field: keyof RateRow, value: string) =>
    onChange(rows.map((r, idx) => (idx === i ? { ...r, [field]: value } : r)));

  return (
    <div className="space-y-2">
      <p className="text-sm font-medium">{label}</p>
      {rows.map((row, i) => (
        <div key={i} className="space-y-1">
          <div className="flex items-center gap-2">
            <select
              value={row.duration}
              onChange={(e) => updateRow(i, 'duration', e.target.value)}
              className="rounded-md border border-input bg-transparent px-3 py-1.5 text-sm"
            >
              {DURATION_OPTIONS.map((d) => (
                <option key={d.value} value={d.value}>
                  {d.label}
                </option>
              ))}
            </select>
            <Input
              placeholder={t('wizard.rate_price_placeholder')}
              type="number"
              min="0"
              value={row.price}
              onChange={(e) => updateRow(i, 'price', e.target.value)}
              className="w-28"
              aria-invalid={!!stepErrors[`${errorPrefix}_${i}`]}
            />
            <button
              type="button"
              onClick={() => removeRow(i)}
              className="text-xs text-destructive hover:underline"
            >
              {t('wizard.rate_remove')}
            </button>
          </div>
          <InputError message={stepErrors[`${errorPrefix}_${i}`]} />
        </div>
      ))}
      <Button type="button" variant="outline" size="sm" onClick={addRow}>
        {t('wizard.rate_add')}
      </Button>
    </div>
  );
}

// ─── Wizard content ───────────────────────────────────────────────────────────

export function WizardContent({ onSuccess }: { onSuccess?: () => void }) {
  const { t } = useTranslation();

  const [step, setStep] = useState<WizardStep>('basics');
  const [form, setForm] = useState<WizardState>(EMPTY);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [processing, setProcessing] = useState(false);

  const [regName, setRegName] = useState('');
  const [regEmail, setRegEmail] = useState('');
  const [regPassword, setRegPassword] = useState('');
  const [regPasswordConfirm, setRegPasswordConfirm] = useState('');

  const hasRestoredRef = useRef(false);

  useEffect(() => {
    if (hasRestoredRef.current) return;
    hasRestoredRef.current = true;
    const saved = loadFromStorage();
    if (saved.display_name || saved.city || saved.description_en) {
      setForm(saved);
    }
  }, []);

  useEffect(() => {
    saveToStorage(form);
  }, [form]);

  const set = <K extends keyof WizardState>(key: K, value: WizardState[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const toggleLanguage = (code: string) =>
    set(
      'languages',
      form.languages.includes(code)
        ? form.languages.filter((l) => l !== code)
        : [...form.languages, code],
    );

  const stepIndex = STEP_IDS.indexOf(step);
  const reg = {
    name: regName,
    email: regEmail,
    password: regPassword,
    passwordConfirm: regPasswordConfirm,
  };

  const goNext = () => {
    const stepErrors = validateStep(step, form, reg, t);
    if (Object.keys(stepErrors).length > 0) {
      setErrors(stepErrors);
      return;
    }
    setErrors({});
    if (stepIndex < STEP_IDS.length - 1) {
      setStep(STEP_IDS[stepIndex + 1]);
    }
  };

  const goBack = () => {
    setErrors({});
    if (stepIndex > 0) {
      setStep(STEP_IDS[stepIndex - 1]);
    }
  };

  const buildProfilePayload = () => {
    const buildRates = (rows: RateRow[]) =>
      rows
        .filter((r) => r.price !== '')
        .map((r) => ({ duration: Number(r.duration), price: r.price }));

    return {
      display_name: form.display_name,
      gender: form.gender,
      age: form.age || null,
      nationality: form.nationality || null,
      city: form.city || null,
      country: form.country || null,
      languages: form.languages,
      subtitle: { en: form.subtitle_en || null },
      description: { en: form.description_en || null },
      contacts: {
        phone: form.contacts_phone || null,
        whatsapp: form.contacts_phone || null,
        telegram: form.contacts_telegram || null,
      },
      rates: {
        in_call: buildRates(form.rates_in_call),
        out_call: buildRates(form.rates_out_call),
        video_call: [],
      },
    };
  };

  const handleSubmit = async () => {
    const stepErrors = validateStep('register', form, reg, t);
    if (Object.keys(stepErrors).length > 0) {
      setErrors(stepErrors);
      return;
    }

    setProcessing(true);
    setErrors({});

    try {
      const regRes = await axios.post(
        '/register',
        {
          name: regName,
          email: regEmail,
          password: regPassword,
          password_confirmation: regPasswordConfirm,
          role: 'independent',
        },
        {
          headers: { 'X-CSRF-TOKEN': getCsrf(), Accept: 'application/json' },
          maxRedirects: 0,
          validateStatus: (s) => s < 500,
        },
      );

      if (regRes.status === 422) {
        setErrors(regRes.data?.errors ?? {});
        setProcessing(false);
        return;
      }

      router.post(
        myProfileRoutes.store().url,
        buildProfilePayload() as Parameters<typeof router.post>[1],
        {
          onSuccess: () => {
            clearWizardStorage();
            onSuccess?.();
          },
          onError: (errs) => {
            setErrors(errs);
            setProcessing(false);
          },
          onFinish: () => setProcessing(false),
        },
      );
    } catch {
      setErrors({ general: t('wizard.err_generic') });
      setProcessing(false);
    }
  };

  const hasSavedData = !!(
    form.display_name ||
    form.city ||
    form.description_en
  );

  // Step labels come from translations
  const stepLabels: Record<WizardStep, string> = {
    basics: t('wizard.step_basics'),
    bio: t('wizard.step_bio'),
    rates: t('wizard.step_rates'),
    contact: t('wizard.step_contact'),
    register: t('wizard.step_register'),
  };

  return (
    <div className="space-y-6">
      {/* Progress bar */}
      <nav aria-label="Progress">
        <ol className="flex items-center gap-1.5">
          {STEP_IDS.map((s, i) => {
            const done = i < stepIndex;
            const current = s === step;
            return (
              <li key={s} className="flex items-center gap-1.5">
                <button
                  type="button"
                  onClick={() => {
                    if (done) {
                      setErrors({});
                      setStep(s);
                    }
                  }}
                  className={[
                    'flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-semibold transition-colors',
                    current
                      ? 'bg-primary text-primary-foreground'
                      : done
                        ? 'cursor-pointer bg-primary/20 text-primary hover:bg-primary/30'
                        : 'cursor-default bg-muted text-muted-foreground',
                  ].join(' ')}
                  aria-current={current ? 'step' : undefined}
                >
                  {done ? <CheckCircle className="h-3.5 w-3.5" /> : i + 1}
                </button>
                <span
                  className={[
                    'hidden text-xs sm:block',
                    current ? 'font-medium' : 'text-muted-foreground',
                  ].join(' ')}
                >
                  {stepLabels[s]}
                </span>
                {i < STEP_IDS.length - 1 && (
                  <span className="h-px w-4 shrink-0 bg-border" aria-hidden />
                )}
              </li>
            );
          })}
        </ol>
      </nav>

      {/* Restore banner */}
      {hasSavedData && step === 'basics' && (
        <div className="flex items-center justify-between rounded-md border border-primary/20 bg-primary/5 px-3 py-2 text-sm">
          <span className="text-primary">{t('wizard.progress_restored')}</span>
          <button
            type="button"
            onClick={() => {
              setForm(EMPTY);
              clearWizardStorage();
            }}
            className="text-xs text-muted-foreground underline hover:text-foreground"
          >
            {t('wizard.start_over')}
          </button>
        </div>
      )}

      {/* ── Step 1: Basics ── */}
      {step === 'basics' && (
        <div className="space-y-4">
          <div>
            <h2 className="text-base font-semibold">
              {t('wizard.heading_basics')}
            </h2>
            <p className="text-sm text-muted-foreground">
              {t('wizard.subheading_basics')}
            </p>
          </div>
          <div className="grid gap-3">
            <div className="grid gap-1.5">
              <Label htmlFor="display_name">
                {t('wizard.display_name_label')}{' '}
                <span className="text-destructive">*</span>
              </Label>
              <Input
                id="display_name"
                value={form.display_name}
                onChange={(e) => set('display_name', e.target.value)}
                placeholder={t('wizard.display_name_placeholder')}
                autoFocus
                aria-invalid={!!errors.display_name}
              />
              <InputError message={errors.display_name} />
            </div>

            <div className="grid gap-1.5">
              <Label>{t('wizard.gender_label')}</Label>
              <div className="flex gap-2">
                {(['female', 'male', 'trans'] as Gender[]).map((g) => (
                  <button
                    key={g}
                    type="button"
                    onClick={() => set('gender', g)}
                    className={[
                      'rounded-full border px-4 py-1.5 text-sm transition-colors',
                      form.gender === g
                        ? 'border-primary bg-primary text-primary-foreground'
                        : 'border-border hover:border-primary',
                    ].join(' ')}
                  >
                    {t(`wizard.gender_${g}`)}
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="grid gap-1.5">
                <Label htmlFor="age">{t('wizard.age_label')}</Label>
                <Input
                  id="age"
                  type="number"
                  min="18"
                  max="99"
                  value={form.age}
                  onChange={(e) => set('age', e.target.value)}
                  placeholder={t('wizard.age_placeholder')}
                  aria-invalid={!!errors.age}
                />
                <InputError message={errors.age} />
              </div>
              <div className="grid gap-1.5">
                <Label htmlFor="nationality">
                  {t('wizard.nationality_label')}
                </Label>
                <Input
                  id="nationality"
                  value={form.nationality}
                  onChange={(e) => set('nationality', e.target.value)}
                  placeholder={t('wizard.nationality_placeholder')}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="grid gap-1.5">
                <Label htmlFor="city">{t('wizard.city_label')}</Label>
                <Input
                  id="city"
                  value={form.city}
                  onChange={(e) => set('city', e.target.value)}
                  placeholder={t('wizard.city_placeholder')}
                />
              </div>
              <div className="grid gap-1.5">
                <Label htmlFor="country">{t('wizard.country_label')}</Label>
                <Input
                  id="country"
                  value={form.country}
                  onChange={(e) => set('country', e.target.value)}
                  placeholder={t('wizard.country_placeholder')}
                />
              </div>
            </div>

            <div className="grid gap-1.5">
              <Label>{t('wizard.languages_label')}</Label>
              <div className="flex flex-wrap gap-1.5">
                {LANGUAGE_OPTIONS.map((lang) => (
                  <button
                    key={lang.code}
                    type="button"
                    onClick={() => toggleLanguage(lang.code)}
                    className={[
                      'rounded-full border px-3 py-1 text-xs transition-colors',
                      form.languages.includes(lang.code)
                        ? 'border-primary bg-primary text-primary-foreground'
                        : 'border-border hover:border-primary',
                    ].join(' ')}
                  >
                    {lang.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Step 2: Bio ── */}
      {step === 'bio' && (
        <div className="space-y-4">
          <div>
            <h2 className="text-base font-semibold">
              {t('wizard.heading_bio')}
            </h2>
            <p className="text-sm text-muted-foreground">
              {t('wizard.subheading_bio')}
            </p>
          </div>
          <div className="grid gap-3">
            <div className="grid gap-1.5">
              <Label htmlFor="subtitle_en">
                {t('wizard.tagline_label')}{' '}
                <span className="text-xs text-muted-foreground">
                  {t('wizard.tagline_hint')}
                </span>
              </Label>
              <Input
                id="subtitle_en"
                value={form.subtitle_en}
                onChange={(e) => set('subtitle_en', e.target.value)}
                placeholder={t('wizard.tagline_placeholder')}
                maxLength={200}
                autoFocus
                aria-invalid={!!errors.subtitle_en}
              />
              <InputError message={errors.subtitle_en} />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="description_en">
                {t('wizard.description_label')}
              </Label>
              <textarea
                id="description_en"
                value={form.description_en}
                onChange={(e) => set('description_en', e.target.value)}
                rows={6}
                maxLength={5000}
                className="rounded-md border border-input bg-transparent px-3 py-2 text-sm placeholder:text-muted-foreground"
                placeholder={t('wizard.description_placeholder')}
                aria-invalid={!!errors.description_en}
              />
              <InputError message={errors.description_en} />
            </div>
          </div>
        </div>
      )}

      {/* ── Step 3: Rates ── */}
      {step === 'rates' && (
        <div className="space-y-4">
          <div>
            <h2 className="text-base font-semibold">
              {t('wizard.heading_rates')}
            </h2>
            <p className="text-sm text-muted-foreground">
              {t('wizard.subheading_rates')}
            </p>
          </div>
          <div className="space-y-5">
            <RateTable
              label={t('wizard.rate_in_call')}
              rows={form.rates_in_call}
              errorPrefix="rate"
              stepErrors={errors}
              onChange={(rows) => set('rates_in_call', rows)}
            />
            <RateTable
              label={t('wizard.rate_out_call')}
              rows={form.rates_out_call}
              errorPrefix="rate"
              stepErrors={errors}
              onChange={(rows) => set('rates_out_call', rows)}
            />
          </div>
        </div>
      )}

      {/* ── Step 4: Contact ── */}
      {step === 'contact' && (
        <div className="space-y-4">
          <div>
            <h2 className="text-base font-semibold">
              {t('wizard.heading_contact')}
            </h2>
            <p className="text-sm text-muted-foreground">
              {t('wizard.subheading_contact')}
            </p>
          </div>
          <div className="grid gap-3">
            <div className="grid gap-1.5">
              <Label htmlFor="contacts_phone">
                {t('wizard.contact_phone_label')}
              </Label>
              <Input
                id="contacts_phone"
                value={form.contacts_phone}
                onChange={(e) => set('contacts_phone', e.target.value)}
                type="tel"
                placeholder={t('wizard.contact_phone_placeholder')}
                autoFocus
                aria-invalid={!!errors.contacts}
              />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="contacts_telegram">
                {t('wizard.contact_telegram_label')}
              </Label>
              <Input
                id="contacts_telegram"
                value={form.contacts_telegram}
                onChange={(e) => set('contacts_telegram', e.target.value)}
                placeholder={t('wizard.contact_telegram_placeholder')}
              />
            </div>
            <InputError message={errors.contacts} />
          </div>
        </div>
      )}

      {/* ── Step 5: Register ── */}
      {step === 'register' && (
        <div className="space-y-4">
          <div>
            <h2 className="text-base font-semibold">
              {t('wizard.heading_register')}
            </h2>
            <p className="text-sm text-muted-foreground">
              {t('wizard.subheading_register')}
            </p>
          </div>

          {/* Summary */}
          <div className="divide-y divide-border rounded-lg border text-sm">
            <div className="flex justify-between px-4 py-2">
              <span className="text-muted-foreground">
                {t('wizard.summary_name')}
              </span>
              <span className="font-medium">{form.display_name || '—'}</span>
            </div>
            <div className="flex justify-between px-4 py-2">
              <span className="text-muted-foreground">
                {t('wizard.summary_location')}
              </span>
              <span>
                {[form.city, form.country].filter(Boolean).join(', ') || '—'}
              </span>
            </div>
            <div className="flex justify-between px-4 py-2">
              <span className="text-muted-foreground">
                {t('wizard.summary_rates')}
              </span>
              <span>
                {form.rates_in_call.length + form.rates_out_call.length > 0
                  ? t('wizard.summary_tiers', {
                      count:
                        form.rates_in_call.length + form.rates_out_call.length,
                    })
                  : '—'}
              </span>
            </div>
            <div className="flex justify-between px-4 py-2">
              <span className="text-muted-foreground">
                {t('wizard.summary_contact')}
              </span>
              <span>
                {form.contacts_phone || form.contacts_telegram || '—'}
              </span>
            </div>
          </div>

          <div className="grid gap-3">
            <div className="grid gap-1.5">
              <Label htmlFor="reg_name">{t('wizard.reg_name_label')}</Label>
              <Input
                id="reg_name"
                value={regName}
                onChange={(e) => setRegName(e.target.value)}
                placeholder={t('wizard.reg_name_placeholder')}
                autoComplete="name"
                autoFocus
                aria-invalid={!!errors.name}
              />
              <InputError message={errors.name} />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="reg_email">{t('wizard.reg_email_label')}</Label>
              <Input
                id="reg_email"
                type="email"
                value={regEmail}
                onChange={(e) => setRegEmail(e.target.value)}
                placeholder={t('wizard.reg_email_placeholder')}
                autoComplete="email"
                aria-invalid={!!errors.email}
              />
              <InputError message={errors.email} />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="reg_password">
                {t('wizard.reg_password_label')}
              </Label>
              <Input
                id="reg_password"
                type="password"
                value={regPassword}
                onChange={(e) => setRegPassword(e.target.value)}
                placeholder={t('wizard.reg_password_placeholder')}
                autoComplete="new-password"
                aria-invalid={!!errors.password}
              />
              <InputError message={errors.password} />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="reg_password_confirm">
                {t('wizard.reg_password_confirm_label')}
              </Label>
              <Input
                id="reg_password_confirm"
                type="password"
                value={regPasswordConfirm}
                onChange={(e) => setRegPasswordConfirm(e.target.value)}
                placeholder={t('wizard.reg_password_confirm_placeholder')}
                autoComplete="new-password"
                aria-invalid={!!errors.password_confirmation}
              />
              <InputError message={errors.password_confirmation} />
            </div>
          </div>

          {errors.general && (
            <p className="text-sm text-destructive">{errors.general}</p>
          )}
        </div>
      )}

      {/* Navigation */}
      <div className="flex justify-between border-t pt-4">
        {stepIndex > 0 ? (
          <Button
            type="button"
            variant="outline"
            onClick={goBack}
            disabled={processing}
          >
            {t('wizard.btn_back')}
          </Button>
        ) : (
          <span />
        )}

        {step !== 'register' ? (
          <Button type="button" onClick={goNext}>
            {t('wizard.btn_continue')}
          </Button>
        ) : (
          <Button
            type="button"
            onClick={handleSubmit}
            disabled={processing}
            className="min-w-40"
          >
            {processing ? (
              <>
                <Spinner />
                {t('wizard.btn_submitting')}
              </>
            ) : (
              t('wizard.btn_submit')
            )}
          </Button>
        )}
      </div>

      {step !== 'register' && (
        <p className="text-center text-xs text-muted-foreground">
          {t('wizard.already_have_account')}{' '}
          <a href="/login" className="underline hover:text-foreground">
            {t('wizard.log_in')}
          </a>
        </p>
      )}
    </div>
  );
}

// ─── Modal wrapper ────────────────────────────────────────────────────────────

export function WizardModal({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] w-full max-w-lg overflow-y-auto sm:max-w-xl">
        <DialogTitle className="sr-only">
          {t('wizard.dialog_title')}
        </DialogTitle>
        <WizardContent onSuccess={() => onOpenChange(false)} />
      </DialogContent>
    </Dialog>
  );
}
